import classNames from "classnames/bind";
import * as React from "react";
import UIIcon, { UIIconProps } from "../UIIcon";

import DefaultErrorView, { DefaultErrorViewProps } from "./DefaultErrorView";
import styles from "./TextInput.module.scss";
import IconButton from "../IconButton";

const cx = classNames.bind(styles);

export type CommonProps = {
    className?: string;
    customErrorView?: React.ReactNode;
    dataTestId?: string;
    hasError?: boolean;
    helperText?: string;
    id?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    isTextareaField?: boolean;
    label?: string;
    labelClassName?: string;
    errorClassName?: string;
    icon?: UIIconProps["icon"];
    alignRight?: boolean;
    type?: string;
    pattern?: string;
    onReset?: () => void;
    showResetButton?: boolean;
    outerLabelClassName?: string;
} & DefaultErrorViewProps;

export type TextInputProps = CommonProps & React.InputHTMLAttributes<HTMLInputElement>;

export type TextareaProps = CommonProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

const TextInput = React.forwardRef<
    HTMLInputElement | HTMLTextAreaElement,
    TextInputProps | TextareaProps
>((props, ref) => {
    const {
        label,
        hasError = false,
        isDisabled = false,
        isRequired = false,
        onChange,
        onKeyDown,
        onPaste,
        placeholder,
        value,
        errorText,
        helperText,
        className,
        maxLength,
        customErrorView,
        name = "",
        labelClassName,
        onFocus,
        onBlur,
        dataTestId,
        errorClassName,
        errorTextClassName,
        errorIconClassName,
        isTextareaField,
        alignRight,
        icon,
        type = "text",
        pattern,
        inputMode,
        onReset,
        showResetButton = false,
        outerLabelClassName,
        autoComplete,
    } = props;

    const inputWithIcon = !isTextareaField && !!props?.icon;

    const basicAttrs = {
        className: cx(className, isTextareaField ? "textarea" : "input", {
            hasError,
            hasValue: value,
            isDisabled,
            inputHasIcon: inputWithIcon,
            alignRight,
        }),
        disabled: isDisabled,
        inputMode,
        onChange,
        onKeyDown,
        onPaste,
        placeholder,
        required: isRequired,
        ref,
        value,
        maxLength,
        name,
        onFocus,
        onBlur,
        "data-testid": dataTestId,
        pattern,
        autoComplete,
    };

    const textareaAttrs = {
        ...basicAttrs,
    } as React.TextareaHTMLAttributes<HTMLTextAreaElement>;
    const inputAttrs = {
        ...basicAttrs,
        type,
    } as React.InputHTMLAttributes<HTMLInputElement>;

    const Label = !label ? "div" : "label";
    const showHelperText = helperText && !hasError;
    const isHidden = type === "hidden";

    let ErrorView;

    if (hasError) {
        ErrorView = customErrorView || (
            <DefaultErrorView
                errorText={errorText}
                errorClassName={errorClassName}
                errorTextClassName={errorTextClassName}
                errorIconClassName={errorIconClassName}
            />
        );
    }

    return (
        <Label
            className={cx("container", outerLabelClassName, { hidden: isHidden })}
            data-testid="FormField"
        >
            {label && (
                <span className={cx("label", labelClassName, { isRequired })} id={name}>
                    {label}
                </span>
            )}

            <span className={cx({ hasIcon: inputWithIcon })}>
                {inputWithIcon && (
                    <span className={cx("iconContainer")}>
                        <UIIcon className={cx("icon")} icon={icon ?? "Person"} />
                    </span>
                )}
                {isTextareaField ? (
                    <textarea {...textareaAttrs} aria-labelledby={name} />
                ) : (
                    <input {...inputAttrs} aria-labelledby={name} />
                )}
            </span>
            {onReset && typeof onReset === "function" && showResetButton && (
                <div className={cx("clearInputWrapper")}>
                    <IconButton
                        icon="Close"
                        onClick={onReset}
                        title="Clear field"
                        aria-label="Clear field"
                    />
                </div>
            )}
            {showHelperText && <span className={cx("helperText")}>{helperText}</span>}
            {ErrorView}
        </Label>
    );
});

TextInput.displayName = "TextInput";

export default TextInput;
